import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSubsetExportData } from '../../../api/hooks/useSubsetExportData/useSubsetExportData';
import { Dataset, GridRow, JSONObject } from '../../../types/types';
import {
  ReportDataSource,
  ReportParentContext,
  ReportRowsBySource,
  ReportTemplate,
} from '../types';
import { promoteRowsToReportMaster } from '../utils/promoteRowsToReportMaster';
import {
  resolveSubsetId,
  resolveTemplateDefinitionSources,
  resolveTemplateItemNames,
  resolveTemplateSources,
} from '../utils/resolveTemplateItemNames';
import {
  resolveSubsetActiveFilters,
  resolveSubsetActiveSorting,
} from '../utils/resolveSubsetActiveFilters';
import {
  describeReportTemplateReferenceIssue,
  findReportTemplateReferenceIssue,
} from '../utils/validateReportTemplateReferences';

interface UseReportRowsParams {
  template: ReportTemplate | undefined;
  dataset: Dataset | undefined;
  activeFilters: Record<string, unknown> | null;
  activeSorting: { field: string; order: number }[];
  datasetControl?: JSONObject | null;
  slaveDatasets: Record<string, Dataset>;
  slaveLazyStates?: Record<string, unknown>;
  /** Grid snapshot to read from when the dataset has no unique key. */
  cachedQueryId?: string;
  /** Present when the report itself was opened from a subordinate grid. */
  parentContext?: ReportParentContext;
}

export type ReportRowsState = {
  rowsBySource: ReportRowsBySource;
  /**
   * Definition per source. Without it a block reading a subset would resolve its
   * column labels and formats against the master definition and silently fall
   * back to raw item names.
   */
  definitionsBySource: Record<string, Dataset>;
  /** Filters actually applied to each subordinate row source. */
  activeFiltersBySource: Record<string, Record<string, unknown>>;
  isLoading: boolean;
  error: Error | undefined;
  /** Sources whose definition was unavailable, so they printed no rows. */
  unresolvedSources: string[];
};

/** Dataset definitions needed to describe active subordinate filters. */
const resolveFilterDefinitionSources = (
  activeFilters: Record<string, unknown> | null
): ReportDataSource[] =>
  Object.keys(activeFilters ?? {}).flatMap((key) => {
    if (!key.endsWith('>')) return [];
    const datasetId = key.slice(0, -1).trim();
    return datasetId ? ([`subset:${datasetId}`] as ReportDataSource[]) : [];
  });

/** Only definition fields that can change the validity or meaning of a layout. */
const datasetDefinitionSignature = (dataset: Dataset | undefined) => {
  const candidateItems = dataset?.items;
  const items = Array.isArray(candidateItems) ? candidateItems : [];
  return {
    datasetId: dataset?.datasetId ?? null,
    items: items.map((item) => ({
      name: item.name,
      type: item.type ?? null,
      filterParam: item.filterParam ?? null,
      formatItem: item.formatItem ?? null,
    })),
  };
};

/**
 * Loads every row a report covers.
 *
 * Deliberately un-paginated: footer totals and the VAT breakdown are computed
 * from these rows, and summing only the page the grid happens to have loaded
 * would print a quietly wrong total. Only the columns the template references
 * are requested.
 */
export const useReportRows = ({
  template,
  dataset,
  activeFilters,
  activeSorting,
  datasetControl,
  slaveDatasets,
  slaveLazyStates = {},
  cachedQueryId,
  parentContext,
}: UseReportRowsParams): ReportRowsState => {
  const { loadDatasetRows, loadSubsetRows, loadDatasetDefinition } =
    useSubsetExportData();
  const [state, setState] = useState<ReportRowsState>({
    rowsBySource: {},
    definitionsBySource: {},
    activeFiltersBySource: {},
    isLoading: true,
    error: undefined,
    unresolvedSources: [],
  });
  // Guards against a resolved fetch writing state after the page moved on.
  const requestIdRef = useRef(0);

  /**
   * The fetch is keyed on the *values* of the inputs, never on their identities.
   *
   * Filters, sorting and the dataset control come out of a React context, which
   * gives no guarantee of referential stability — a caller writing the perfectly
   * ordinary `activeSorting ?? []` hands over a fresh array on every render. If
   * the effect keyed on identity, that alone would spin it forever: run, set
   * loading, re-render, new identity, run again — and each pass aborts the
   * request the previous one started, so no row ever arrives. The page freezes
   * and prints an empty report. Keyed on values, an unstable caller costs
   * nothing.
   */
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        // Stored templates created from the same default may share the layout's
        // internal templateId. The actual fetch identity is the set of sources
        // and item names this concrete layout references.
        templateItems: template
          ? resolveTemplateSources(template).map((source) => ({
              source,
              itemNames: resolveTemplateItemNames(template, source),
            }))
          : null,
        datasetId: dataset?.datasetId ?? null,
        masterDefinition: datasetDefinitionSignature(dataset),
        subsetDefinitions: template
          ? Array.from(
              new Set([
                ...resolveTemplateSources(template),
                ...resolveTemplateDefinitionSources(template),
                ...resolveFilterDefinitionSources(activeFilters),
              ])
            )
              .map(resolveSubsetId)
              .filter((subsetId): subsetId is string => Boolean(subsetId))
              .map((subsetId) => ({
                subsetId,
                definition: datasetDefinitionSignature(slaveDatasets[subsetId]),
              }))
          : [],
        activeFilters: activeFilters ?? null,
        activeSorting: activeSorting ?? [],
        datasetControl: datasetControl ?? null,
        slaveLazyStates,
        cachedQueryId: cachedQueryId ?? null,
        parentDataset: datasetDefinitionSignature(parentContext?.dataset),
        parentRow: parentContext?.row ?? null,
      }),
    [
      template,
      dataset,
      slaveDatasets,
      activeFilters,
      activeSorting,
      datasetControl,
      slaveLazyStates,
      cachedQueryId,
      parentContext,
    ]
  );

  // Read current inputs and loaders without making their identities effect dependencies.
  const latestRef = useRef({
    template,
    dataset,
    activeFilters,
    activeSorting,
    datasetControl,
    slaveDatasets,
    slaveLazyStates,
    cachedQueryId,
    parentContext,
    loadDatasetRows,
    loadSubsetRows,
    loadDatasetDefinition,
  });
  latestRef.current = {
    template,
    dataset,
    activeFilters,
    activeSorting,
    datasetControl,
    slaveDatasets,
    slaveLazyStates,
    cachedQueryId,
    parentContext,
    loadDatasetRows,
    loadSubsetRows,
    loadDatasetDefinition,
  };

  const loadRows = useCallback(
    async (signal: AbortSignal): Promise<ReportRowsState> => {
      const {
        template,
        dataset,
        activeFilters,
        activeSorting,
        datasetControl,
        slaveDatasets,
        slaveLazyStates,
        cachedQueryId,
        parentContext,
        loadDatasetRows,
        loadSubsetRows,
        loadDatasetDefinition,
      } = latestRef.current;

      if (!template || !dataset) {
        return {
          rowsBySource: {},
          definitionsBySource: {},
          activeFiltersBySource: {},
          isLoading: false,
          error: undefined,
          unresolvedSources: [],
        };
      }

      const rowsBySource: ReportRowsBySource = {};
      const definitionsBySource: Record<string, Dataset> = { master: dataset };
      const activeFiltersBySource: Record<string, Record<string, unknown>> = {};
      const unresolvedSources: string[] = [];
      const invalidTemplateSources: string[] = [];
      const rowSources = resolveTemplateSources(template);
      const templateDefinitionSources =
        resolveTemplateDefinitionSources(template);
      const requiredDefinitionSourceSet = new Set([
        ...rowSources,
        ...templateDefinitionSources,
      ]);
      const definitionSources = Array.from(
        new Set([
          ...rowSources,
          ...templateDefinitionSources,
          ...resolveFilterDefinitionSources(activeFilters),
        ])
      );
      // Only lock the snapshot when there is no unique key to order by.
      const lockedCachedQueryId = dataset.idItem ? undefined : cachedQueryId;

      for (const source of definitionSources) {
        const subsetId = resolveSubsetId(source);
        if (!subsetId) {
          continue;
        }

        const subsetDataset =
          slaveDatasets[subsetId] ??
          (await loadDatasetDefinition({
            subsetDatasetId: subsetId,
            signal,
          }));

        if (!subsetDataset) {
          // Without a definition the columns cannot be resolved; report it
          // rather than printing an empty section as if it had no data.
          unresolvedSources.push(source);
          if (requiredDefinitionSourceSet.has(source)) {
            invalidTemplateSources.push(source);
          }
          continue;
        }

        definitionsBySource[source] = subsetDataset;
        const subsetActiveFilters = resolveSubsetActiveFilters(
          slaveLazyStates,
          subsetId,
          subsetDataset
        );
        if (subsetActiveFilters) {
          activeFiltersBySource[source] = subsetActiveFilters;
        }
      }

      if (invalidTemplateSources.length > 0) {
        throw new Error(
          `Invalid report template source: ${invalidTemplateSources.join(', ')}`
        );
      }

      const referenceIssue = findReportTemplateReferenceIssue(
        template,
        definitionsBySource
      );
      if (referenceIssue) {
        throw new Error(describeReportTemplateReferenceIssue(referenceIssue));
      }

      // Validate every stored reference before loading any rows. Otherwise a
      // removed aggregate column can reach the renderer as an apparently valid
      // zero, which is worse than a controlled print error.
      const masterItemNames = resolveTemplateItemNames(template, 'master');
      const includeMasterId = rowSources.some((source) => source !== 'master');
      const masterRows = parentContext
        ? promoteRowsToReportMaster(
            dataset,
            await loadSubsetRows({
              masterDataset: parentContext.dataset,
              masterRows: [parentContext.row],
              dataset,
              activeSorting,
              activeFilters,
              includeLimit: false,
              itemNames: masterItemNames,
              // Use the child id as the grouping key for nested sources.
              includeIdItem: true,
              signal,
              reportErrors: true,
            })
          )
        : await loadDatasetRows({
            dataset,
            activeSorting,
            activeFilters,
            datasetControl: datasetControl ?? undefined,
            includeLimit: false,
            itemNames: masterItemNames,
            // Master ids are needed to fetch subset rows per master row.
            includeIdItem: includeMasterId,
            lockedCachedQueryId,
            signal,
            // A report must never print an empty sheet because the fetch failed:
            // "no data" and "could not load" look identical on paper.
            reportErrors: true,
          });
      rowsBySource.master = masterRows;

      for (const source of rowSources) {
        const subsetId = resolveSubsetId(source);
        if (!subsetId) continue;
        const subsetDataset = definitionsBySource[source];
        rowsBySource[source] = await loadSubsetRows({
          masterDataset: dataset,
          masterRows,
          dataset: subsetDataset,
          activeSorting: resolveSubsetActiveSorting(
            slaveLazyStates,
            subsetId,
            subsetDataset
          ),
          activeFilters: activeFiltersBySource[source],
          itemNames: resolveTemplateItemNames(template, source),
          includeIdItem: false,
          signal,
          reportErrors: true,
        });
      }

      return {
        rowsBySource,
        definitionsBySource,
        activeFiltersBySource,
        isLoading: false,
        error: undefined,
        unresolvedSources,
      };
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setState((previous) => ({
      ...previous,
      isLoading: true,
      error: undefined,
    }));

    loadRows(controller.signal)
      .then((nextState) => {
        if (requestId === requestIdRef.current) {
          setState(nextState);
        }
      })
      .catch((error: unknown) => {
        if (requestId !== requestIdRef.current || controller.signal.aborted) {
          return;
        }
        setState({
          rowsBySource: {},
          definitionsBySource: {},
          activeFiltersBySource: {},
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
          unresolvedSources: [],
        });
      });

    return () => controller.abort();
    // requestKey, not the raw inputs: see the comment on requestKey.
  }, [requestKey, loadRows]);

  return state;
};

/** Rows for a source, or an empty list when the source produced none. */
export const getSourceRows = (
  rowsBySource: ReportRowsBySource,
  source: string
): GridRow[] => rowsBySource[source] ?? [];
